// internationalization
import { useTranslation } from 'react-i18next';

// material ui icons
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';

// material ui
import { Badge, Tooltip } from '@mui/material';

import { DateTime } from 'luxon';

const InitiativeTimestampSummaryIndicator = ({
  timestampSummary,
  initiative,
}) => {
  const { t } = useTranslation('energyxplorer-initiatives-tab');
  if (!timestampSummary || !initiative) return null;

  const { maxEarliestDataTimestamp, minLatestDataTimestamp } = timestampSummary;

  const baselineStart = DateTime.fromISO(
    initiative.baselineStart || '',
  ).setZone('utc');
  const comparisonEnd = DateTime.fromISO(
    initiative.comparisonEnd || '',
  ).setZone('utc');
  const maxEarliest = DateTime.fromISO(maxEarliestDataTimestamp || '').setZone(
    'utc',
  );
  const minLatest = DateTime.fromISO(minLatestDataTimestamp || '').setZone(
    'utc',
  );
  const now = DateTime.now().setZone('utc');

  const state = initiative.state;

  const hasValidBaselineCheck = baselineStart.isValid && maxEarliest.isValid;
  const hasValidComparisonCheck = comparisonEnd.isValid && minLatest.isValid;

  const shouldCheckBaseline =
    state !== 'Created' && state !== 'Archived' && hasValidBaselineCheck;

  const shouldCheckComparison = state === 'Done' && hasValidComparisonCheck;

  // Rule 1: baselineStart must be > maxEarliestDataTimestamp
  const baselineIssue = shouldCheckBaseline && baselineStart <= maxEarliest;

  // Rule 2: comparisonEnd must be < minLatestDataTimestamp (only for "Done")
  const comparisonIssue = shouldCheckComparison && comparisonEnd >= minLatest;

  let tooltipTitle = '';
  let BadgeIcon = null;
  let badgeColor = 'warning';

  if (baselineIssue) {
    // Rule 1: use Warning badge
    BadgeIcon = WarningRoundedIcon;
    badgeColor = 'warning';
    tooltipTitle = t(
      'initiative_timestamp_summary_warning.start_before_sync_period',
    );
  } else if (comparisonIssue) {
    // Rule 2: split by age of comparisonEnd
    const diffDays = now.diff(comparisonEnd, 'days').days;

    if (diffDays >= 0 && diffDays < 30) {
      // Less than 30 days old -> Info badge
      BadgeIcon = InfoRoundedIcon;
      badgeColor = 'info';
      tooltipTitle = t(
        'initiative_timestamp_summary_warning.partial_sync_warning',
      );
    } else if (diffDays >= 30) {
      // 30+ days old -> Warning badge
      BadgeIcon = WarningRoundedIcon;
      badgeColor = 'warning';
      tooltipTitle = t(
        'initiative_timestamp_summary_warning.end_before_sync_period',
      );
    } else {
      // comparisonEnd is in the future – treat as info/warning too
      BadgeIcon = InfoRoundedIcon;
      badgeColor = 'info';
      tooltipTitle = t(
        'initiative_timestamp_summary_warning.missing_data_warning',
      );
    }
  }

  // Nothing to show
  if (!BadgeIcon || !tooltipTitle) return null;

  return (
    <Tooltip
      title={tooltipTitle}
      slotProps={{
        tooltip: {
          sx: {
            fontSize: '14px', // affects the tooltip text
          },
        },
      }}
    >
      <Badge
        overlap="circular"
        badgeContent={<BadgeIcon fontSize="small" color={badgeColor} />}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <AccessTimeRoundedIcon />
      </Badge>
    </Tooltip>
  );
};

export default InitiativeTimestampSummaryIndicator;
